import React, { useState, useEffect, useCallback } from "react";
import {
  Save,
  RefreshCw,
  AlertCircle,
  Check,
  MessageSquare,
  Info,
  Zap,
  Sparkles,
  Plus,
  Trash2,
  Image as ImageIcon,
  LayoutGrid,
} from "lucide-react";
import {
  getChatSettings,
  updateChatSettings,
  getImageUploadSettings,
  updateImageUploadSettings,
  getCarouselSettings,
  updateCarouselSettings,
} from "../services/settingsApi";

/**
 * Chat Settings Admin Page
 *
 * This page manages global chat configuration settings.
 * Settings apply to all chatbot conversations.
 *
 * Key settings:
 * - welcome_message: Initial greeting shown when chat starts
 * - suggestion_buttons: Quick action buttons shown with welcome message
 * - max_response_words: Limits AI response length
 * - session_timeout_minutes: How long before inactive sessions expire
 * - enable_location_search: Allow users to search for store locations
 * - enable_item_search: Allow users to search for menu items
 * - fallback_to_llm: Use AI when no specific handler matches
 * - fallback_message: Message shown when pipeline fails
 */
const Settings = () => {
  // Loading and UI states
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  // Chat configuration settings
  const [chatFormData, setChatFormData] = useState({
    max_response_words: 45,
    enable_location_search: true,
    enable_item_search: true,
    fallback_to_llm: true,
    session_timeout_minutes: 20,
    welcome_message: "",
    suggestion_buttons: [{ label: "", action: "", source: "backend", type: "other" }],
    fallback_message: "",
    fallback_suggestions: [],
    // Optional time-based overrides for welcome message
    time_based_greetings: {
      morning: "",
      afternoon: "",
      evening: "",
      night: "",
    },
  });
  const [isChatSaving, setIsChatSaving] = useState(false);

  // Image upload settings
  const [imageFormData, setImageFormData] = useState({
    image_default_width: 600,
    image_default_height: 600,
    image_max_size_bytes: 1048576, // 1MB
    allowed_formats: ["image/jpeg", "image/png", "image/webp"],
  });
  const [isImageSaving, setIsImageSaving] = useState(false);
  const [maxSizeMB, setMaxSizeMB] = useState("1.00");

  // Carousel settings
  const [carouselFormData, setCarouselFormData] = useState({
    max_items: 10,
  });
  const [isCarouselSaving, setIsCarouselSaving] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  // Load settings from API
  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch settings from backend in parallel
      const [chatResponse, imageResponse, carouselResponse] = await Promise.all([
        getChatSettings(),
        getImageUploadSettings(),
        getCarouselSettings(),
      ]);

      const chatData = chatResponse.data.data;

      // Normalize time-based greetings from backend (may be null/undefined)
      const tbg = chatData.time_based_greetings || {};

      // Update form with loaded settings
      setChatFormData({
        max_response_words: chatData.max_response_words,
        enable_location_search: chatData.enable_location_search,
        enable_item_search: chatData.enable_item_search ?? chatData.enable_product_search ?? true,
        fallback_to_llm: chatData.fallback_to_llm,
        session_timeout_minutes: chatData.session_timeout_minutes || 20,
        welcome_message: chatData.welcome_message ?? "",
        suggestion_buttons:
          chatData.suggestion_buttons && chatData.suggestion_buttons.length > 0
            ? chatData.suggestion_buttons.map((btn) => ({
                label: btn.label || "",
                action: btn.action || "",
                source: btn.source || "backend",
                type: btn.type || "other",
              }))
            : [{ label: "", action: "", source: "backend", type: "other" }],
        fallback_message: chatData.fallback_message ?? "",
        fallback_suggestions: chatData.fallback_suggestions || [],
        time_based_greetings: {
          morning: tbg.morning || "",
          afternoon: tbg.afternoon || "",
          evening: tbg.evening || "",
          night: tbg.night || "",
        },
      });

      const imageData = imageResponse.data.data;

      setImageFormData({
        image_default_width: imageData.image_default_width,
        image_default_height: imageData.image_default_height,
        image_max_size_bytes: imageData.image_max_size_bytes,
        allowed_formats: imageData.allowed_formats || ["image/jpeg", "image/png", "image/webp"],
      });
      setMaxSizeMB((imageData.image_max_size_bytes / (1024 * 1024)).toFixed(2));

      // Load carousel settings
      const carouselData = carouselResponse.data.data;
      setCarouselFormData({
        max_items: carouselData.max_items || 10,
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      setError("Failed to load settings");
      showToast("Failed to load settings", "error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Save settings to API
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsChatSaving(true);
      
      // Clean up the data by removing empty suggestion buttons and ensuring source is backend
      const cleanedData = {
        ...chatFormData,
        suggestion_buttons: chatFormData.suggestion_buttons
          .filter((b) => b.label?.trim() !== "" && b.action?.trim() !== "")
          .map((b) => ({
            label: b.label.trim(),
            action: b.action.trim(),
            source: "backend",
            type: b.type || "other",
          })),
      };
      
      await updateChatSettings(cleanedData);
      showToast("Chat settings saved successfully!", "success");
    } catch (error) {
      console.error("Error saving chat settings:", error);
      showToast("Failed to save chat settings", "error");
    } finally {
      setIsChatSaving(false);
    }
  };

  // Handle form input changes
  const handleChatChange = (e) => {
    const { name, value, type, checked } = e.target;
    setChatFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseInt(value, 10) : value,
    }));
  };

  // Welcome suggestion button handlers
  const handleSuggestionChange = (index, field, value) => {
    setChatFormData((prev) => {
      const newButtons = [...prev.suggestion_buttons];
      newButtons[index] = { ...newButtons[index], [field]: value };
      return { ...prev, suggestion_buttons: newButtons };
    });
  };

  const addSuggestionButton = () => {
    setChatFormData((prev) => ({
      ...prev,
      suggestion_buttons: [
        ...prev.suggestion_buttons,
        { label: "", action: "", source: "backend", type: "other" },
      ],
    }));
  };

  const removeSuggestionButton = (index) => {
    setChatFormData((prev) => ({
      ...prev,
      suggestion_buttons: prev.suggestion_buttons.filter((_, i) => i !== index),
    }));
  };

  const handleTimeGreetingChange = (period, value) => {
    setChatFormData((prev) => ({
      ...prev,
      time_based_greetings: {
        ...(prev.time_based_greetings || {}),
        [period]: value,
      },
    }));
  };

  // Carousel settings handlers
  const handleCarouselChange = (e) => {
    const { name, value } = e.target;
    setCarouselFormData((prev) => ({
      ...prev,
      [name]: parseInt(value, 10),
    }));
  };

  const handleCarouselSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsCarouselSaving(true);
      await updateCarouselSettings(carouselFormData);
      showToast("Carousel settings saved successfully!", "success");
    } catch (error) {
      console.error("Error saving carousel settings:", error);
      showToast("Failed to save carousel settings", "error");
    } finally {
      setIsCarouselSaving(false);
    }
  };

  // Image upload settings handlers
  const handleImageChange = (e) => {
    const { name, value } = e.target;
    setImageFormData((prev) => ({
      ...prev,
      [name]: parseInt(value, 10),
    }));
  };

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    
    // Format and validate maxSizeMB before submission
    const mb = parseFloat(maxSizeMB);
    if (!isNaN(mb) && mb >= 0.01 && mb <= 10) {
      const clampedMB = Math.max(0.01, Math.min(10, mb));
      const formattedMB = clampedMB.toFixed(2);
      setMaxSizeMB(formattedMB);
      setImageFormData((prev) => ({
        ...prev,
        image_max_size_bytes: Math.round(clampedMB * 1024 * 1024),
      }));
    } else {
      // Invalid value - restore last valid and show error
      setMaxSizeMB((imageFormData.image_max_size_bytes / (1024 * 1024)).toFixed(2));
      showToast("Please enter a valid file size between 0.01 and 10 MB", "error");
      return;
    }
    
    try {
      setIsImageSaving(true);
      await updateImageUploadSettings(imageFormData);
      showToast("Image upload settings saved successfully!", "success");
    } catch (error) {
      console.error("Error saving image upload settings:", error);
      showToast("Failed to save image upload settings", "error");
    } finally {
      setIsImageSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-[#6b4226]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3.5 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-red-800">Error</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="grid gap-6">
        {/* Welcome Message Card */}
        <form
          onSubmit={handleChatSubmit}
          className="bg-white rounded-lg shadow p-5 space-y-5 border border-gray-100"
        >
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Sparkles className="text-purple-600" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Welcome Message</h3>
          </div>

          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Info className="text-indigo-600" size={18} />
                <div>
                  <p className="text-sm font-medium text-gray-800">Time-based Welcome Messages</p>
                  <p className="text-xs text-gray-600">
                    Customize welcome messages for different times of day. These messages will be shown to users when they start a chat.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Morning Message (00:00–11:59)
                  </label>
                  <textarea
                    value={chatFormData.time_based_greetings.morning}
                    onChange={(e) => handleTimeGreetingChange("morning", e.target.value)}
                    placeholder="Good morning! Welcome to our coffee shop..."
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Afternoon Message (12:00–16:59)
                  </label>
                  <textarea
                    value={chatFormData.time_based_greetings.afternoon}
                    onChange={(e) => handleTimeGreetingChange("afternoon", e.target.value)}
                    placeholder="Good afternoon! Welcome to our coffee shop..."
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Evening Message (17:00–23:59)
                  </label>
                  <textarea
                    value={chatFormData.time_based_greetings.evening}
                    onChange={(e) => handleTimeGreetingChange("evening", e.target.value)}
                    placeholder="Good evening! Welcome to our coffee shop..."
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Quick Action Buttons
                </label>
                <button
                  type="button"
                  onClick={addSuggestionButton}
                  className="px-3 py-1.5 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 text-sm"
                >
                  <Plus size={16} />
                  Add Button
                </button>
              </div>
              <div className="space-y-3">
                {chatFormData.suggestion_buttons.map((button, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={button.label}
                        onChange={(e) => handleSuggestionChange(index, "label", e.target.value)}
                        placeholder="Button Label"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                      />
                      <input
                        type="text"
                        value={button.action}
                        onChange={(e) => handleSuggestionChange(index, "action", e.target.value)}
                        placeholder="Button Action (what to send)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="w-28">
                      <select
                        value={button.type || "other"}
                        onChange={(e) => handleSuggestionChange(index, "type", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent text-sm"
                      >
                        <option value="food">food</option>
                        <option value="drink">drink</option>
                        <option value="store">store</option>
                        <option value="merchandise">merchandise</option>
                        <option value="other">other</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSuggestionButton(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={chatFormData.suggestion_buttons.length <= 1}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </form>

        {/* Chat Settings Card */}
        <form
          onSubmit={handleChatSubmit}
          className="bg-white rounded-lg shadow p-5 space-y-5 border border-gray-100"
        >
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <MessageSquare className="text-[#6b4226]" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Chat Settings</h3>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Response Words
              </label>
              <input
                type="number"
                name="max_response_words"
                value={chatFormData.max_response_words}
                onChange={handleChatChange}
                min="10"
                max="200"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum number of words in chatbot responses (10-200)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                name="session_timeout_minutes"
                value={chatFormData.session_timeout_minutes}
                onChange={handleChatChange}
                min="1"
                max="120"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Session expires after this many minutes of inactivity (1-120)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fallback Message
              </label>
              <input
                type="text"
                name="fallback_message"
                value={chatFormData.fallback_message}
                onChange={handleChatChange}
                placeholder="I'm here to help! What would you like to know?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
              />
            </div>

            <div className="space-y-3 lg:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="enable_location_search"
                  checked={chatFormData.enable_location_search}
                  onChange={handleChatChange}
                  className="w-4 h-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">
                  Enable Location Search
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="enable_item_search"
                  checked={chatFormData.enable_item_search}
                  onChange={handleChatChange}
                  className="w-4 h-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">Enable Item Search</label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="fallback_to_llm"
                  checked={chatFormData.fallback_to_llm}
                  onChange={handleChatChange}
                  className="w-4 h-4 text-[#6b4226] border-gray-300 rounded focus:ring-[#6b4226]"
                />
                <label className="ml-3 text-sm font-medium text-gray-700">
                  Fallback to LLM (GPT)
                </label>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fallback Suggestions (JSON)
            </label>
            <textarea
              name="fallback_suggestions"
              value={JSON.stringify(chatFormData.fallback_suggestions, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setChatFormData({ ...chatFormData, fallback_suggestions: parsed });
                } catch {
                  // Invalid JSON - let user fix it
                }
              }}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent font-mono text-sm"
              placeholder='[{"label": "Menu", "action": "Menu"}]'
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={fetchSettings}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              disabled={isLoading || isChatSaving}
            >
              <RefreshCw
                size={18}
                className={isLoading ? "animate-spin text-[#6b4226]" : "text-[#6b4226]"}
              />
              Reset
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 font-semibold disabled:opacity-50"
              disabled={isChatSaving}
            >
              {isChatSaving ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>

        {/* Carousel Settings Card */}
        <form
          onSubmit={handleCarouselSubmit}
          className="bg-white rounded-lg shadow p-5 space-y-5 border border-gray-100"
        >
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <LayoutGrid className="text-green-600" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Carousel Settings</h3>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Items in Carousel
              </label>
              <input
                type="number"
                name="max_items"
                value={carouselFormData.max_items}
                onChange={handleCarouselChange}
                min="1"
                max="20"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum number of items shown in carousel cards (1-20). Default: 10
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-sm font-medium text-gray-800">About Carousel Limit</p>
                <p className="text-xs text-gray-600 mt-1">
                  This setting controls how many items can be displayed in a single carousel when users search for menu items or stores. 
                  Increasing this value allows more items to be shown, but may impact mobile performance.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={fetchSettings}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
              disabled={isLoading || isCarouselSaving}
            >
              <RefreshCw
                size={18}
                className={isLoading ? "animate-spin text-[#6b4226]" : "text-[#6b4226]"}
              />
              Reset
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 font-semibold disabled:opacity-50"
              disabled={isCarouselSaving}
            >
              {isCarouselSaving ? (
                <>
                  <RefreshCw size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>

        {/* Image Upload Settings Card */}
        <form
          onSubmit={handleImageSubmit}
          className="bg-white rounded-lg shadow p-5 space-y-5 border border-gray-100"
        >
          <div className="flex items-center gap-3 pb-3 border-b">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <ImageIcon className="text-blue-600" size={20} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Image Upload Settings</h3>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Image Width (px)
              </label>
              <input
                type="number"
                name="image_default_width"
                value={imageFormData.image_default_width}
                onChange={handleImageChange}
                min="100"
                max="2000"
                step="50"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Images will be resized to this width (100-2000px)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Image Height (px)
              </label>
              <input
                type="number"
                name="image_default_height"
                value={imageFormData.image_default_height}
                onChange={handleImageChange}
                min="100"
                max="2000"
                step="50"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Images will be resized to this height (100-2000px)
              </p>
            </div>

            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum File Size (MB)
              </label>
              <input
                type="number"
                name="image_max_size_bytes"
                value={maxSizeMB}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty input while typing
                  if (value === "" || value === ".") {
                    setMaxSizeMB(value);
                    return;
                  }
                  
                  // Allow free typing - store as string
                  setMaxSizeMB(value);
                  
                  // Only update bytes if value is valid number within range
                  const mb = parseFloat(value);
                  if (!isNaN(mb) && mb >= 0.01 && mb <= 10) {
                    setImageFormData((prev) => ({
                      ...prev,
                      image_max_size_bytes: Math.round(mb * 1024 * 1024),
                    }));
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  
                  // Handle empty input - restore last valid value
                  if (value === "" || value === ".") {
                    setMaxSizeMB((imageFormData.image_max_size_bytes / (1024 * 1024)).toFixed(2));
                    return;
                  }
                  
                  // Parse and validate
                  const mb = parseFloat(value);
                  
                  if (isNaN(mb)) {
                    // Invalid number - restore last valid value
                    setMaxSizeMB((imageFormData.image_max_size_bytes / (1024 * 1024)).toFixed(2));
                    return;
                  }
                  
                  // Clamp value to valid range
                  const clampedMB = Math.max(0.01, Math.min(10, mb));
                  
                  // Always format to 2 decimal places on blur (e.g., "1" becomes "1.00")
                  setMaxSizeMB(clampedMB.toFixed(2));
                  setImageFormData((prev) => ({
                    ...prev,
                    image_max_size_bytes: Math.round(clampedMB * 1024 * 1024),
                  }));
                }}
                min="0.01"
                max="10"
                step="any"
                placeholder="1.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b4226] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum allowed file size after resize (10-10240KB, input in MB)
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              type="submit"
              disabled={isImageSaving}
              className="px-5 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#4a2c1a] transition-colors flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isImageSaving ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
              toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
            }`}
          >
            {toast.type === "success" ? <Check size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
